import React, { useEffect, useState, useMemo } from 'react';
import { stockService } from '../services/api';
import './Inventory.css';

const Inventory = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCat, setSelectedCat] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'designation', direction: 'asc' });

    useEffect(() => {
        stockService.getAllProducts()
            .then(data => {
                setProducts(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Erreur API:", err);
                setLoading(false);
            });
    }, []);

    const categories = useMemo(() => {
        return [...new Set(products.map(p => p.categorie))].filter(Boolean).sort();
    }, [products]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleQuickUpdate = async (id, newQty) => {
        const qty = parseInt(newQty);
        if (isNaN(qty) || qty < 0) return;
        setProducts(prev => prev.map(p => p.id === id ? { ...p, stock_actuel: qty } : p));
        await stockService.updateStock(id, qty);
    };

    // --- C'EST ICI QU'ON APPELLE LE SERVICE QUI APPELLE LE PHP ---
    const handleDeleteProduct = async (id, designation) => {
        if (window.confirm(`Supprimer cette ligne spécifique pour : ${designation} ?`)) {
            // 1. Suppression visuelle immédiate
            setProducts(prev => prev.filter(p => p.id !== id));

            // 2. Appel au PHP avec l'ID
            try {
                const res = await stockService.deleteProduct(id);
                if (!res.success) {
                    alert("Erreur : " + res.message);
                }
            } catch (error) {
                alert("Erreur de connexion");
            }
        }
    };
    const filteredAndSortedProducts = useMemo(() => {
        let items = products.filter(p => {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch =
                p.designation.toLowerCase().includes(searchLower) ||
                p.ref_produit.toLowerCase().includes(searchLower) ||
                (p.code_barre && p.code_barre.toLowerCase().includes(searchLower));
            const matchesCat = selectedCat === '' || p.categorie === selectedCat;
            return matchesSearch && matchesCat;
        });

        if (sortConfig.key !== null) {
            items.sort((a, b) => {
                let aValue, bValue;
                if (sortConfig.key === 'statut') {
                    aValue = parseInt(a.stock_actuel) <= parseInt(a.stock_min) ? 0 : 1;
                    bValue = parseInt(b.stock_actuel) <= parseInt(b.stock_min) ? 0 : 1;
                } else if (sortConfig.key === 'valeur') {
                    aValue = parseFloat(a.stock_actuel) * parseFloat(a.prix_achat_ht || 0);
                    bValue = parseFloat(b.stock_actuel) * parseFloat(b.prix_achat_ht || 0);
                } else {
                    aValue = a[sortConfig.key] || '';
                    bValue = b[sortConfig.key] || '';
                    if (!isNaN(aValue) && !isNaN(bValue) && aValue !== '' && bValue !== '') {
                        aValue = parseFloat(aValue);
                        bValue = parseFloat(bValue);
                    } else {
                        aValue = aValue.toString().toLowerCase();
                        bValue = bValue.toString().toLowerCase();
                    }
                }
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [products, searchTerm, selectedCat, sortConfig]);

    if (loading) return <div className="inventory-container" style={{ display: 'flex', justifyContent: 'center' }}><h2>Chargement...</h2></div>;

    return (
        <div className="inventory-container">
            <header className="inventory-header">
                <h1>Gestion de Stock</h1>
                <div className="inventory-controls">
                    <div className="filter-group">
                        <label>Catégorie</label>
                        <select className="custom-select" value={selectedCat} onChange={(e) => setSelectedCat(e.target.value)}>
                            <option value="">Tout</option>
                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                    </div>
                    <div className="search-box">
                        <label className="filter-group"><span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#ade8f4' }}>RECHERCHE</span></label>
                        <input type="text" placeholder="..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                </div>
            </header>

            <div className="table-wrapper">
                <table className="custom-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('ref_produit')} className="sortable-th">Réf {sortConfig.key === 'ref_produit' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                            <th onClick={() => requestSort('code_barre')} className="sortable-th">Code {sortConfig.key === 'code_barre' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                            <th onClick={() => requestSort('designation')} className="sortable-th">Nom {sortConfig.key === 'designation' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                            <th onClick={() => requestSort('stock_actuel')} className="sortable-th">Stock {sortConfig.key === 'stock_actuel' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                            <th onClick={() => requestSort('statut')} className="sortable-th">État {sortConfig.key === 'statut' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                            <th onClick={() => requestSort('valeur')} className="sortable-th">Val {sortConfig.key === 'valeur' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                            <th style={{ textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAndSortedProducts.map(p => (
                            <tr key={p.id}>
                                <td data-label="Réf">{p.ref_produit}</td>
                                <td data-label="Code">{p.code_barre || '-'}</td>
                                <td data-label="Nom" className="bold">{p.designation}</td>
                                <td data-label="Stock">
                                    <div className="stock-counter">
                                        <button className="btn-qty" onClick={() => handleQuickUpdate(p.id, parseInt(p.stock_actuel) - 1)}>-</button>
                                        <input type="number" className="input-stock-table" value={p.stock_actuel} onChange={(e) => handleQuickUpdate(p.id, e.target.value)} />
                                        <button className="btn-qty" onClick={() => handleQuickUpdate(p.id, parseInt(p.stock_actuel) + 1)}>+</button>
                                    </div>
                                </td>
                                <td data-label="État">
                                    <span className={`badge ${parseInt(p.stock_actuel) <= parseInt(p.stock_min) ? 'danger' : 'success'}`}>
                                        {parseInt(p.stock_actuel) <= parseInt(p.stock_min) ? '⚠' : 'OK'}
                                    </span>
                                </td>
                                <td data-label="Val">{(parseFloat(p.stock_actuel) * parseFloat(p.prix_achat_ht || 0)).toFixed(2)}€</td>
                                <td data-label="Action" style={{ textAlign: 'center' }}>
                                    <button
                                        className="btn-trash"
                                        onClick={() => handleDeleteProduct(p.id, p.designation)}
                                    >
                                        🗑️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Inventory;